import * as React from 'react';
import { useTranslation } from 'react-i18next';
import ISettingContextType from 'src/models/interfaces/ISettingContextType';
import SettingsModel from 'src/models/settingsModel';
import { AuthTypes } from "src/models/types/AuthTypes";
import { ProviderPropsTypes } from 'src/models/types/ProviderPropsTypes';
import UserModel from "src/models/userModel";
import { LoginService } from 'src/services/Login.service';

import * as authService from 'src/services/authService';
import { InteractionStatus } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { useErrorSetter } from "src/context/ErrorProvider";
import { useNotifications } from 'src/context/NotificationProvider';

const SettingContext = React.createContext<ISettingContextType>({} as ISettingContextType);

/**
 * @function StateProvider
 * @description Provider Estado del usuario.
 * @param {React.ReactNode} children
 * @returns {React.FC<ProviderPropsTypes>}
 */
export const StateProvider: React.FC<ProviderPropsTypes> = ({ children }: ProviderPropsTypes) => {
  const [lang, setLang] = React.useState<string>("EN");
  const [listFunc, setListFunc] = React.useState<(string | number)[]>([]);
  const [user, setUser] = React.useState<UserModel | null>(null);
  const [status, setStatus] = React.useState<AuthTypes>("checking");
  const setError = useErrorSetter();
  const { instance, inProgress } = useMsal();
  const [graphData] = React.useState(null);
  const { getLogin } = LoginService();
  const { i18n } = useTranslation();
  const { fetchUnread } = useNotifications();

  React.useEffect(() => {
    /**
     * @function GetUserData
     * @description Obtiene los datos de la base de datos.
     * @param {any} data Datos del usuario logueado en Azure.
     */
    function GetUserData(data: any): void {
      if (graphData) data = graphData;
      if (!data) return;
      getLogin(data)
        .then((setting: SettingsModel) => {
          setLang(setting.getLang());
          setUser(setting.getUser());
          setListFunc(setting.getIdFunctions());
          if (setting.userIsValid()) {
            setStatus("authenticated");
            fetchUnread();
          } else {
            setStatus("no-authenticated");
          }
        }).catch((err) => {
          setError(err);
          setStatus("no-authenticated");
        });
    }

    if (!graphData && inProgress === InteractionStatus.None) {
      authService.callMsGraph().then((data: any) => {
        if (!data.error) {
          authService.callProfilePhoto()
            .then((image: any) => {
              const blob = new Blob([image], { type: "image/jpeg" })
              const urlProfilePhoto = URL.createObjectURL(blob);
              data.profilePhoto = urlProfilePhoto;
              GetUserData(data);
            })
        }
      }).catch((err) => {
        console.error(err);
      });
    }
  }, [inProgress, graphData, instance]);

  React.useEffect(() => {
    i18n.changeLanguage(lang.toLowerCase());
  }, [lang]);

  /**
   * @function StateProvider.logout
   * @description Elimina los datos del usuario y la lista de funciones de accesso del usuario.
   */
  const logout = () => {
    setUser(null);
    setListFunc([]);
    setStatus("closed-session");
  };

  /**
   * @function StateProvider.login
   * @description Habilita el relogueo del usuario.
   */
  const login = () => {
    instance.logoutPopup(
      {
        postLogoutRedirectUri: "/",
        mainWindowRedirectUri: "/",
      }
    );
  }

  /**
   * @function containAssignations
   * @description Retorna si al menos posee una función asignada
   * @return True si tiene al menos una función, false si no posee funciones.
   */
  const containAssignations = (): boolean => {
    return listFunc.filter(x => +x > 0).length === 0;
  }

  /**
   * @function existFunction
   * @description Verifica si existe la función en la lista de funciones.
   * @param {string | number} id Id de la funcion a verificar.
   * @returns true si el id recibido existe en la lista de funciones, false en caso contrario. 
   */
  const existFunction = (id: string | number): boolean => {
    if (id === -1) return true;
    return listFunc.indexOf(id) !== -1;
  }

  return (
    <SettingContext.Provider value={{ lang, setLang, user, existFunction, status, setStatus, setError, logout, login, containAssignations }}>
      {children}
    </SettingContext.Provider>
  );
}

/**
 * @function StateContext
 * @description Retorna el contexto de datos de usuario.
 *      - lang: Get language
 *      - setLang: Set language
 *      - user: Get User data
 *      - listFunc: Set User data
 *      - status: Get la palabra clave para identificar el layout a renderizar.
 *      - setStatus: Setea la palabra clave para identificar el layout a renderizar.
 *      - error: Get lista de errores a mostrar en el panel.
 *      - setError: Agrega un error a la lista de errores.
 *      - logout: Cierra la seción.
 * @returns {ISettingContextType}
 * @example const { lang, setLang, user, listFunc, status, setStatus, error, setError: addError, logout } = StateContext();
 */
export default function StateContext(): ISettingContextType {
  return React.useContext(SettingContext);
}
